// analytics.js — fire-and-forget event tracking layer
// Never throws, never blocks UI, batches for efficiency.

const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_BATCH_SIZE = 20;
const ENDPOINT = '/api/events/track/';

// ─── Session ID ───────────────────────────────────────────────
function getSessionId() {
  try {
    let id = sessionStorage.getItem('pp_session_id');
    if (!id) {
      id = 'ss_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9);
      sessionStorage.setItem('pp_session_id', id);
    }
    return id;
  } catch {
    return 'ss_unknown';
  }
}

// ─── Queue ────────────────────────────────────────────────────
let queue = [];
let flushTimer = null;

function getBaseUrl() {
  return (process.env.REACT_APP_API_URL || 'https://api.picprofile.com') + ENDPOINT;
}

function getAuthHeader() {
  try {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function flushQueue() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await fetch(getBaseUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ events: batch }),
    });
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('[analytics] flush failed:', err);
  }
}

function flushViaBeacon() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
    navigator.sendBeacon(getBaseUrl(), blob);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') console.warn('[analytics] beacon failed:', err);
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL_MS);
}

// Flush on page unload — sendBeacon is reliable even during navigation
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushViaBeacon);
}

// ─── Public API ───────────────────────────────────────────────
/**
 * trackEvent(eventType, options?)
 * @param {string} eventType  — one of the defined event type strings
 * @param {object} [opts]     — { postId, targetUserId, metadata }
 */
export function trackEvent(eventType, opts = {}) {
  try {
    const { postId, targetUserId, metadata } = opts;
    const event = {
      event_type: eventType,
      session_id: getSessionId(),
      ...(postId != null       && { post_id: postId }),
      ...(targetUserId != null && { target_user_id: targetUserId }),
      ...(metadata             && { metadata }),
    };
    queue.push(event);
    if (queue.length >= FLUSH_BATCH_SIZE) {
      flushQueue();
    } else {
      scheduleFlush();
    }
  } catch {
    // silent
  }
}
